import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Building2,
  Loader2,
  Pencil,
  Users,
  UsersRound,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TeamFormDialog from "@/components/teams/TeamFormDialog";
import TeamMultiSelect, {
  type TeamMultiSelectOption,
} from "@/components/teams/TeamMultiSelect";
import TeamOverviewPanel from "@/components/teams/TeamOverviewPanel";
import { ROTULO_DO_NIVEL } from "@/lib/access-level";
import { useAuth } from "@/contexts/auth.context";
import { toast } from "@/hooks/use-toast";
import {
  ehAcessoNegado,
  ehNaoEncontrado,
  mensagemDeErro,
} from "@/lib/team-errors";
import teamService, { type TeamDetail } from "@/services/team.service";
import {
  listAccountsApiV1AccountsGet,
  listAccountUsersApiV1MyAccountUsersGet,
} from "@/backend-sdk/client/sdk.gen";
import type {
  ManagedAccountListItem,
  MyAccountUserItem,
} from "@/backend-sdk/client/types.gen";
import { heyAdminApiClient } from "@/lib/hey-api-client";
import { formatDateBR } from "@/utils/dateFormat";

/**
 * A tela de um time: a composição e o painel consolidado.
 *
 * Como a lista, ela tem duas faces. **Quem administra** vê as duas abas e edita os
 * vínculos; **o membro** vê só o consolidado, porque `GET /api/v1/teams/{id}` responde
 * `403` para ele — e por isso a tela dele nem chama a rota: o nome do time vem do escopo
 * que o contexto de autenticação já carregou.
 *
 * As duas listas de candidatos vêm com o token principal (`heyAdminApiClient`), e não com o
 * da conta em que o operador estiver: os membros possíveis são os funcionários da **conta
 * principal** da agência — exatamente os que `GET /my-account/users` lista para o titular —,
 * e as lojas possíveis são as da agência. Com o token de uma subconta, a primeira lista
 * traria os usuários daquela loja e a segunda responderia `403`.
 */
const TeamDetailPage = () => {
  const { teamId = "" } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { canManageTeams, teamScope, loading: perfilCarregando } = useAuth();

  const [team, setTeam] = useState<TeamDetail | null>(null);
  const [loading, setLoading] = useState(canManageTeams);
  const [erro, setErro] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const [usuarios, setUsuarios] = useState<MyAccountUserItem[]>([]);
  const [lojas, setLojas] = useState<ManagedAccountListItem[]>([]);
  const [carregandoCandidatos, setCarregandoCandidatos] = useState(false);

  const [membrosSelecionados, setMembrosSelecionados] = useState<string[]>([]);
  const [lojasSelecionadas, setLojasSelecionadas] = useState<string[]>([]);
  const [salvandoMembros, setSalvandoMembros] = useState(false);
  const [salvandoLojas, setSalvandoLojas] = useState(false);

  /** O time visto pelo membro — só o que `my-scope` publica. */
  const timeDoEscopo = useMemo(
    () => (teamScope?.teams ?? []).find((item) => item.id === teamId) ?? null,
    [teamScope, teamId],
  );

  const aplicar = useCallback((detalhe: TeamDetail) => {
    setTeam(detalhe);
    setMembrosSelecionados(detalhe.members.map((membro) => membro.user_id));
    setLojasSelecionadas(detalhe.accounts.map((loja) => loja.account_id));
  }, []);

  const carregar = useCallback(async () => {
    if (!canManageTeams || !teamId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      aplicar(await teamService.get(teamId));
      setErro(null);
    } catch (error) {
      setErro(
        ehNaoEncontrado(error)
          ? "Este time não existe, ou não é desta agência."
          : ehAcessoNegado(error)
            ? "Você não administra os times desta agência."
            : mensagemDeErro(error, "Não foi possível carregar o time."),
      );
    } finally {
      setLoading(false);
    }
  }, [canManageTeams, teamId, aplicar]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!canManageTeams) return;

    let cancelado = false;
    const carregarCandidatos = async () => {
      setCarregandoCandidatos(true);
      try {
        const [respostaUsuarios, respostaLojas] = await Promise.all([
          listAccountUsersApiV1MyAccountUsersGet({
            client: heyAdminApiClient,
            headers: { "x-ignore-global-filters": "1" },
            throwOnError: true,
          }),
          listAccountsApiV1AccountsGet({
            client: heyAdminApiClient,
            headers: { "x-ignore-global-filters": "1" },
            throwOnError: true,
          }),
        ]);
        if (cancelado) return;
        setUsuarios(respostaUsuarios.data.data);
        setLojas(respostaLojas.data.accounts ?? []);
      } catch (error) {
        if (!cancelado) {
          console.error("Erro ao carregar candidatos do time:", error);
        }
      } finally {
        if (!cancelado) setCarregandoCandidatos(false);
      }
    };

    carregarCandidatos();
    return () => {
      cancelado = true;
    };
  }, [canManageTeams]);

  const entradaNoTime = useMemo(() => {
    const mapa = new Map<string, string>();
    (team?.members ?? []).forEach((membro) => {
      mapa.set(membro.user_id, membro.joined_at);
    });
    return mapa;
  }, [team]);

  /**
   * Só quem é **equipe** pode ser membro de um time.
   *
   * `GET /my-account/users` lista as duas naturezas de credencial desde que a agência passou
   * a poder ceder acesso ao cliente da loja, e a credencial cedida não entra em time nenhum
   * — `TeamService` a recusa com `400`. Oferecê-la aqui seria oferecer um erro; o filtro é o
   * mesmo do servidor, na tela.
   */
  const candidatosAMembro = usuarios.filter(
    (usuario) => usuario.access_type === "team",
  );

  const opcoesDeMembro: TeamMultiSelectOption[] = candidatosAMembro.map(
    (usuario) => {
      const desde = entradaNoTime.get(usuario.user_id);
      return {
        id: usuario.user_id,
        label: usuario.name,
        hint: desde
          ? `${usuario.email} · no time desde ${formatDateBR(desde)}`
          : usuario.email,
        // O que a pessoa pode fazer nas lojas do time — `viewer` só acompanha, `editor` e
        // `admin` também escrevem. Era o antigo `role`, que não decidia escrita nenhuma.
        badge: ROTULO_DO_NIVEL[usuario.access_level] ?? usuario.access_level,
        muted: usuario.status?.toLowerCase() === "inactive",
      };
    },
  );

  const opcoesDeLoja: TeamMultiSelectOption[] = lojas.map((loja) => ({
    id: loja.id,
    label: loja.name,
    hint: loja.slug,
    badge: loja.status,
    muted: loja.status?.toLowerCase() !== "active",
  }));

  const membrosMudaram =
    team !== null &&
    !mesmoConjunto(
      membrosSelecionados,
      team.members.map((membro) => membro.user_id),
    );

  const lojasMudaram =
    team !== null &&
    !mesmoConjunto(
      lojasSelecionadas,
      team.accounts.map((loja) => loja.account_id),
    );

  const salvarMembros = async () => {
    if (!team) return;
    setSalvandoMembros(true);
    try {
      aplicar(await teamService.setMembers(team.id, membrosSelecionados));
      toast({ title: "Membros do time atualizados." });
    } catch (error) {
      toast({
        title: "Erro ao salvar os membros",
        description: mensagemDeErro(error, "Tente novamente."),
        variant: "destructive",
      });
    } finally {
      setSalvandoMembros(false);
    }
  };

  const salvarLojas = async () => {
    if (!team) return;
    setSalvandoLojas(true);
    try {
      aplicar(await teamService.setAccounts(team.id, lojasSelecionadas));
      toast({ title: "Lojas do time atualizadas." });
    } catch (error) {
      toast({
        title: "Erro ao salvar as lojas",
        description: mensagemDeErro(error, "Tente novamente."),
        variant: "destructive",
      });
    } finally {
      setSalvandoLojas(false);
    }
  };

  const nome = team?.name ?? timeDoEscopo?.name ?? "Time";
  const descricao = team?.description ?? timeDoEscopo?.description ?? null;
  const podeVer = canManageTeams || timeDoEscopo !== null;

  if (perfilCarregando) {
    return (
      <div className="space-y-4 p-4 sm:p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!podeVer) {
    return (
      <div className="p-4 sm:p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="mx-auto mb-3 h-10 w-10 text-amber-500" />
            <p className="font-medium">Você não tem acesso a este time.</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/dashboard/teams")}
            >
              Voltar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 mb-1 text-muted-foreground"
            onClick={() => navigate("/dashboard/teams")}
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Times
          </Button>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            <UsersRound className="h-8 w-8 shrink-0" />
            <span className="truncate">{nome}</span>
            {team && !team.is_active && (
              <Badge variant="outline">Inativo</Badge>
            )}
          </h1>
          {descricao && <p className="text-muted-foreground">{descricao}</p>}
        </div>
        {canManageTeams && team && (
          <Button variant="outline" onClick={() => setShowForm(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            Editar time
          </Button>
        )}
      </header>

      {erro ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="mx-auto mb-3 h-10 w-10 text-amber-500" />
            <p className="font-medium">{erro}</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-72" />
          <Skeleton className="h-96 w-full" />
        </div>
      ) : (
        <Tabs defaultValue={canManageTeams ? "composicao" : "consolidado"}>
          <TabsList
            className={`grid w-full ${canManageTeams ? "grid-cols-2 lg:w-[420px]" : "grid-cols-1 lg:w-[220px]"}`}
          >
            {canManageTeams && (
              <TabsTrigger
                value="composicao"
                className="flex items-center gap-2"
              >
                <Users className="h-4 w-4" />
                Composição
              </TabsTrigger>
            )}
            <TabsTrigger
              value="consolidado"
              className="flex items-center gap-2"
            >
              <BarChart3 className="h-4 w-4" />
              Painel consolidado
            </TabsTrigger>
          </TabsList>

          {canManageTeams && team && (
            <TabsContent value="composicao" className="mt-6 space-y-6">
              <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Membros
                    </CardTitle>
                    <CardDescription>
                      Os funcionários da agência que acompanham as lojas deste
                      time. A lista é a mesma de Minha Conta → Usuários.
                    </CardDescription>
                  </div>
                  <Button
                    onClick={salvarMembros}
                    disabled={!membrosMudaram || salvandoMembros}
                  >
                    {salvandoMembros && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Salvar membros
                  </Button>
                </CardHeader>
                <CardContent>
                  <TeamMultiSelect
                    options={opcoesDeMembro}
                    selectedIds={membrosSelecionados}
                    onChange={setMembrosSelecionados}
                    loading={carregandoCandidatos}
                    searchPlaceholder="Buscar por nome ou e-mail"
                    emptyMessage="A agência ainda não tem funcionários. Crie um em Minha Conta → Usuários."
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5" />
                      Lojas
                    </CardTitle>
                    <CardDescription>
                      As lojas que este time atende. Os membros passam a
                      alcançá-las na requisição seguinte, em modo somente
                      leitura.
                    </CardDescription>
                  </div>
                  <Button
                    onClick={salvarLojas}
                    disabled={!lojasMudaram || salvandoLojas}
                  >
                    {salvandoLojas && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Salvar lojas
                  </Button>
                </CardHeader>
                <CardContent>
                  <TeamMultiSelect
                    options={opcoesDeLoja}
                    selectedIds={lojasSelecionadas}
                    onChange={setLojasSelecionadas}
                    loading={carregandoCandidatos}
                    searchPlaceholder="Buscar loja"
                    emptyMessage="A agência ainda não tem lojas cadastradas."
                  />
                </CardContent>
              </Card>
            </TabsContent>
          )}

          <TabsContent value="consolidado" className="mt-6">
            <TeamOverviewPanel teamId={teamId} />
          </TabsContent>
        </Tabs>
      )}

      {team && (
        <TeamFormDialog
          open={showForm}
          onOpenChange={setShowForm}
          team={team}
          onSaved={carregar}
        />
      )}
    </div>
  );
};

/** Dois conjuntos de ids são o mesmo? A ordem não conta; a seleção é conjunto. */
const mesmoConjunto = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const doA = new Set(a);
  return b.every((id) => doA.has(id));
};

export default TeamDetailPage;
